/**
 * ThreadPoint GA4 Custom Event Tracking  (v2 — 2026-06-09)
 * ==========================================================
 * 1. contact_us_booking     - Calendly booking for the 30min intro call
 * 2. deliverability_booking - Calendly booking for the deliverability A/B test
 * 3. engaged_blog_reader    - 45s+ engagement on /blogs/ pages
 * 4. page_type dimension    - auto-classifies every page as blog, service, industry, or core
 * 5. blog_cta_click         - tracks clicks from blog posts to conversion/service pages
 *
 * Also: basic bot detection to reduce spam traffic.
 *
 * v2 changes:
 *   - Calendly booking detection now checks the embedded Calendly URL
 *     (not just page path) so bookings from /pages/email-deliverability-services
 *     and any other page with a deliverability embed are correctly classified.
 *   - Added /pages/email-deliverability-services to CONVERSION_PAGES.
 */
(function() {
  'use strict';

  /* ---------- helpers ---------- */
  var path = window.location.pathname;
  var gtag = window.gtag;

  /* Wait for gtag if it hasn't loaded yet */
  function sendEvent(name, params) {
    if (typeof window.gtag === 'function') {
      window.gtag('event', name, params || {});
    }
  }

  /* ---------- 4: Page type classification ---------- */
  var SERVICE_PAGES = [
    '/pages/ecommerce-email-sms-marketing-services',
    '/pages/ecommerce-sms-marketing-services',
    '/pages/email-sms-consulting',
    '/pages/email-and-klaviyo-audit',
    '/pages/email-deliverability-services',
    '/pages/klaviyo-agency-services',
    '/pages/klaviyo-flow-setup-email-automation-management',
    '/pages/klaviyo-migration-services',
    '/pages/retention-marketing-agency',
    '/pages/shopify-email-sms-marketing-services',
    '/pages/bottom-funnel-marketing-agency'
  ];

  var CONVERSION_PAGES = [
    '/pages/contact',
    '/pages/deliverability',
    '/pages/email-deliverability-services'
  ];

  function classifyPage(p) {
    p = (p || path).split('?')[0].replace(/\/+$/, '');
    if (p.indexOf('/blogs/') === 0) {
      return p.split('/').length > 3 ? 'blog' : 'blog_index';
    }
    if (p.indexOf('/pages/email-marketing-for-') === 0) return 'industry';
    for (var i = 0; i < SERVICE_PAGES.length; i++) {
      if (p === SERVICE_PAGES[i]) return 'service';
    }
    for (var j = 0; j < CONVERSION_PAGES.length; j++) {
      if (p === CONVERSION_PAGES[j]) return 'conversion';
    }
    return 'core';
  }

  /* Set page_type on all subsequent GA4 events for this pageview */
  var pageType = classifyPage();
  if (typeof window.gtag === 'function') {
    window.gtag('set', {page_type: pageType});
  }

  /* ---------- 1 & 2: Calendly booking tracking ---------- */
  function isCalendlyEvent(e) {
    return e.origin === 'https://calendly.com' &&
           e.data && e.data.event &&
           e.data.event === 'calendly.event_scheduled';
  }

  /**
   * Detect which Calendly event type is embedded on this page by
   * inspecting the data-url attribute on .calendly-inline-widget elements.
   * This is more reliable than page-path matching because the same
   * Calendly can appear on multiple pages (e.g. /pages/deliverability
   * AND /pages/email-deliverability-services).
   */
  function detectCalendlyType() {
    var widgets = document.querySelectorAll('.calendly-inline-widget[data-url]');
    for (var i = 0; i < widgets.length; i++) {
      var url = (widgets[i].getAttribute('data-url') || '').toLowerCase();
      if (url.indexOf('/free-deliverabilty') !== -1 ||
          url.indexOf('/deliverability') !== -1) {
        return 'deliverability';
      }
      if (url.indexOf('/30min') !== -1) {
        return 'contact';
      }
    }
    return null;
  }

  window.addEventListener('message', function(e) {
    if (!isCalendlyEvent(e)) return;

    var eventName = 'calendly_booking_other';
    var bookingType = 'other';

    /* Step 1: Try to classify by embedded Calendly URL (most reliable) */
    var calType = detectCalendlyType();
    if (calType === 'contact') {
      eventName = 'contact_us_booking';
      bookingType = 'general_contact';
    } else if (calType === 'deliverability') {
      eventName = 'deliverability_booking';
      bookingType = 'deliverability_code';
    }

    /* Step 2: Fall back to page path if widget detection failed */
    if (eventName === 'calendly_booking_other') {
      if (path.indexOf('/pages/contact') === 0) {
        eventName = 'contact_us_booking';
        bookingType = 'general_contact';
      } else if (path.indexOf('/pages/deliverability') === 0 ||
                 path.indexOf('/pages/email-deliverability') === 0) {
        eventName = 'deliverability_booking';
        bookingType = 'deliverability_code';
      }
    }

    sendEvent(eventName, {
      page_location: window.location.href,
      booking_type: bookingType
    });
  });

  /* ---------- 3: Blog engagement timer (45 seconds) ---------- */
  if (path.indexOf('/blogs/') === 0 && path.split('/').length > 3) {
    /* Only fire on individual blog posts, not the blog index */
    var blogTimerFired = false;
    var startTime = Date.now();
    var activeTime = 0;
    var lastTick = startTime;
    var isActive = true;

    /* Track visibility so we only count active time */
    document.addEventListener('visibilitychange', function() {
      if (document.hidden) {
        activeTime += Date.now() - lastTick;
        isActive = false;
      } else {
        lastTick = Date.now();
        isActive = true;
      }
    });

    /* Check every 5 seconds */
    var blogTimer = setInterval(function() {
      if (blogTimerFired) { clearInterval(blogTimer); return; }

      var total = activeTime + (isActive ? Date.now() - lastTick : 0);
      if (total >= 45000) {
        blogTimerFired = true;
        sendEvent('engaged_blog_reader', {
          page_location: window.location.href,
          page_title: document.title,
          engagement_seconds: Math.round(total / 1000)
        });
        clearInterval(blogTimer);
      }
    }, 5000);
  }

  /* ---------- 5: Blog CTA click tracking ---------- */
  if (pageType === 'blog') {
    document.addEventListener('click', function(e) {
      var link = e.target.closest ? e.target.closest('a') : null;
      if (!link || !link.href) return;

      var dest;
      try { dest = new URL(link.href); } catch(err) { return; }

      /* Only track internal links */
      if (dest.hostname !== window.location.hostname) return;

      var destType = classifyPage(dest.pathname);
      if (destType === 'service' || destType === 'industry' || destType === 'conversion') {
        sendEvent('blog_cta_click', {
          page_location: window.location.href,
          page_title: document.title,
          link_url: dest.pathname,
          link_text: (link.textContent || '').trim().substring(0, 100),
          destination_type: destType
        });
      }
    });
  }

  /* ---------- Bot traffic flagging ---------- */
  /*
   * Flags suspected bot sessions with a custom event parameter.
   * GA4's built-in bot filter catches IAB bots; this catches
   * the cheap scrapers and spam that slip through.
   */
  (function detectBot() {
    var signals = 0;

    /* webdriver flag (Selenium, Puppeteer, Playwright) */
    if (navigator.webdriver) signals += 3;

    /* No language set */
    if (!navigator.language && !navigator.languages) signals += 2;

    /* Suspicious screen size (headless defaults) */
    if (window.screen && (window.screen.width === 0 || window.screen.height === 0)) signals += 2;

    /* No mouse/touch/keyboard events after 10 seconds */
    var interacted = false;
    var interactHandler = function() {
      interacted = true;
      document.removeEventListener('mousemove', interactHandler);
      document.removeEventListener('touchstart', interactHandler);
      document.removeEventListener('keydown', interactHandler);
      document.removeEventListener('scroll', interactHandler);
    };
    document.addEventListener('mousemove', interactHandler);
    document.addEventListener('touchstart', interactHandler);
    document.addEventListener('keydown', interactHandler);
    document.addEventListener('scroll', interactHandler);

    /* After 10s, send a dimension if bot is suspected */
    setTimeout(function() {
      if (!interacted) signals += 2;
      if (signals >= 3) {
        sendEvent('bot_suspected', {
          bot_score: signals,
          page_location: window.location.href
        });
      }
    }, 10000);
  })();

})();
